import { useContext, useEffect, useState } from 'react'
import { message } from 'antd'

import { commonHelper } from 'helpers'
import { FormsContext } from 'contexts'
import { commonUtils } from 'utils'

import { useGetCustomerInfo } from 'services/clients/clients.service'
import useGeneral from './useGeneral'
import { addEditFormMutation } from 'gql/client'
import { useCustomMutation } from 'services/shared/mutation'
import { TAddEditFormMutationPayload } from 'services/shared/mutation.payload'
import { FORMS_ENUM } from 'enum/form.enum'

const useCustomerDetails = () => {
  const urlParams = new URLSearchParams(window.location.search)
  const idNumber = Number(urlParams.get('idNumber'))
  const { customerToEdit } = useContext(FormsContext)

  const { customer, loading: formInitialDataLoading } = useGetCustomerInfo(idNumber, customerToEdit)

  const addEditCustomer = useCustomMutation<TAddEditFormMutationPayload>({
    mutation: addEditFormMutation,
    refetchQueries: ['getCustomerFirstInfo', 'getClientFormsProgress'],
  })
  const [loading, setLoading] = useState(formInitialDataLoading)
  const [formData, setFormData] = useState({
    title: customer?.title,
    firstName: customer?.firstName,
    middleNames: customer?.middleNames,
    lastName: customer?.lastName,
    relationshipToOtherCustomer: customer?.relationshipToOtherCustomer,
    gender: customer?.gender,
    dateOfBirth: customer?.dateOfBirth,
    maritalStatus: customer?.maritalStatus,
    nationality: customer?.nationality,
    livedHereSinceBirth: customer?.livedHereSinceBirth,
    hasRightToRemain: customer?.hasRightToRemain,
    landlineTelephone: customer?.landlineTelephone,
    mobileTelephone: customer?.mobileTelephone,
    email: customer?.email,
    preferredContactMethod: customer?.preferredContactMethod,
  })

  useEffect(() => {
    window.scroll({ top: 0, behavior: 'smooth' })
  }, [loading])

  useEffect(() => {
    setLoading(formInitialDataLoading)
    if (customer) {
      const newData = commonUtils.removeTypeNameFromObject(customer)
      delete newData._id
      setFormData({ ...newData })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formInitialDataLoading])

  const handleCustomSelectChange = (name: string, value: string) => {
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }))
  }

  const handleAntdFormItemChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target?.name
    let value: string | number | boolean
    if (name === 'landlineTelephone' || name === 'mobileTelephone') {
      value = String(e.target?.value)
    } else {
      value = commonUtils.parseValue(e.target?.value, e.target?.name)
    }
    setFormData((prevState) => ({
      ...prevState,
      [e.target.name]: value,
    }))
  }

  const handleSubmit = async () => {
    const { errors } = await addEditCustomer({
      idNumber,
      customer: customerToEdit,
      formName: FORMS_ENUM.customerDetails,
      values: {
        customerDetails: formData,
      },
    })
    if (errors?.length) {
      setLoading(false)
      const errMessage = commonHelper.parseGraphqlErrorMessage(errors)
      message.error(errMessage)
      return
    }
    message.success('Operation finished successfully')
  }

  const { handleSaveAndGoBack, handleSave, handleSaveAndContinue } = useGeneral({ handleSubmit, setLoading })

  const [saveType, setSaveType] = useState<'save' | 'saveAndGoBack' | 'saveAndContinue'>('save')

  const onSubmit = () => {
    if (saveType === 'saveAndGoBack') {
      handleSaveAndGoBack()
    } else if (saveType === 'saveAndContinue') {
      handleSaveAndContinue()
    } else if (saveType === 'save') {
      handleSave()
    }
  }

  const onFinishFailed = () => {
    message.error('please fill out every required field')
  }

  return {
    onFinishFailed,
    onSubmit,
    setSaveType,
    loading,
    handleAntdFormItemChange,
    handleCustomSelectChange,
    formData,
  }
}

export default useCustomerDetails
